import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  useTheme,
} from '@mui/material';

import { getCoursesState } from '../../../../../store/V2/CoursesSlice';
import { createCourseTemplateAsAdmin } from '../../../../../store/V2/CourseTemplatesSlice';
import { useAppSelector } from '../../../../../hooks';
import { TPTextField } from '../../../../../components/Inputs';

interface AddCourseTemplateModalProps {
  open: boolean;
  onClose: () => void;
}

type TState = {
  name: string;
};

const initialState: TState = {
  name: '',
};

const AddCourseTemplateModal: React.FC<AddCourseTemplateModalProps> = ({
  open,
  onClose,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  // State
  const [state, setState] = useState<TState>({ ...initialState });
  const [isLoading, setLoading] = useState(false);

  // Store
  const coursesState = useAppSelector(getCoursesState);

  const courseData = coursesState.adminCourse;
  const isSubmitDisabled = !state.name.trim().length;

  useEffect(() => {
    if (courseData) {
      setState((prevState) => ({
        ...prevState,
        name: courseData.title,
      }));
    }
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const toggleLoading = () => {
    setLoading((prevState) => !prevState);
  };

  const handleSubmit = async () => {
    if (courseData) {
      toggleLoading();
      await dispatch(
        createCourseTemplateAsAdmin({
          course: courseData._id,
          name: state.name.trim(),
        })
      );
    }

    toggleLoading();
    onClose();
  };

  return (
    <Dialog fullWidth open={open} onClose={onClose} maxWidth="sm">
      <DialogTitle>New Course Template</DialogTitle>
      <DialogContent sx={{ pt: `${theme.spacing(2)} !important` }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <TPTextField
                label="Template Name"
                placeholder="Template Name"
                InputLabelProps={{ shrink: true }}
                value={state.name}
                onChange={(event) =>
                  updateStateAttributes({
                    name: event.currentTarget.value,
                  })
                }
              />
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: '16px' }}>
        <LoadingButton
          size="small"
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          loading={isLoading}
          disabled={isSubmitDisabled}
        >
          Save
        </LoadingButton>
        <Button
          size="small"
          variant="contained"
          color="inherit"
          onClick={onClose}
        >
          Dismiss
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCourseTemplateModal;
